import { format } from 'date-fns';
import { ShoppingBag } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssistedOrderDto } from '@/Domain/api/Conversation/dto/AssistedOrder.dto';

type Props = {
  assistedOrder: AssistedOrderDto;
  timestamp: string;
};

const formatAmount = (amount: string, currency: string): string => {
  const value = Number(amount);
  if (Number.isNaN(value)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

export const AssistedOrderItem = memo(({ assistedOrder, timestamp }: Props) => {
  const { t } = useTranslation(['inbox']);
  const { shopify_order_name, total_amount, currency, shopify_admin_url } = assistedOrder;

  const content = (
    <div className="grid grid-cols-[min-content_1fr] gap-3.5 p-4 border border-success/20 bg-success-muted text-foreground rounded-xl text-sm w-fit self-center">
      <ShoppingBag className="mt-0.5 h-4 w-4 text-success" />
      <div className="flex flex-col">
        <p className="text-sm font-semibold text-success mb-1">{t('assisted_order.label')}</p>
        <span className="font-medium text-foreground">
          {t('assisted_order.order_name', { order_name: shopify_order_name })} ·{' '}
          {formatAmount(total_amount, currency)}
        </span>
        <span className="text-xs text-muted-foreground mt-0.5">
          {format(new Date(timestamp), 'MMM d, h:mm a')}
        </span>
      </div>
    </div>
  );

  if (shopify_admin_url === null) {
    return (
      <li data-testid="assisted-order" className="flex">
        {content}
      </li>
    );
  }

  return (
    <li data-testid="assisted-order" className="flex">
      <a
        href={shopify_admin_url}
        target="_blank"
        rel="noopener noreferrer"
        title={t('assisted_order.view_in_shopify')}
        className="mx-auto hover:opacity-90 transition-opacity"
      >
        {content}
      </a>
    </li>
  );
});

AssistedOrderItem.displayName = 'AssistedOrderItem';
